import { $log } from '../../y/debug';
import { onAttach, onDetach } from '../attach';
import { $el } from '../elements';
import { UIKey } from '../key';
import { uiHidePopup, uiShowPopup, UIPopupOptions } from '../popup';
import { UIRelPos } from '../types';
import { UIView, UIViewSpec } from '../view';
import { UIDropdownItem } from './dropdown';
import { UIAnyInputWrapper } from './input-wrapper';

export type UIGroupDropdownViewGenerator<Model> = (value: Model, dropdown: UIGroupDropdown<Model>) => UIDropdownItem | undefined;

export interface UIValueGroup<Value> {
    name: string;
    values: Value[];
}

export interface UIGroupDropdownOptions {
    keep_open?: boolean;
    search?: boolean;
    search_input?: UIAnyInputWrapper;
}

interface Entry<Model> {
    view: UIViewSpec;
    model: Model;
    selectable: boolean;
    search?: string;
    on_choose?: () => void | boolean;
    hidden?: boolean;
}

export class UIGroupDropdown<Model = any> extends UIView {
    private _options: UIGroupDropdownOptions;
    private _selectable: Entry<Model>[];
    private _selected: number;

    on_choose?: (m: Model, v: UIViewSpec) => void | boolean;

    constructor(groups: UIValueGroup<Model>[], generator: UIGroupDropdownViewGenerator<Model>, options: UIGroupDropdownOptions = {}) {
        super('div', 'ui-group-dropdown');

        this._options = options;
        this._selected = -1;

        const selectable: Entry<Model>[] = [];

        for(const g of groups) {
            const entries: Entry<Model>[] = [];
            const views: UIViewSpec[] = [];
            for(const v of g.values) {
                const item = generator(v, this);
                if(item) {
                    const entry = { view: item.view, model: v, selectable: !item.disabled && !!item.view, search: item.search, on_choose: item.on_choose };
                    entries.push(entry);
                    views.push(item.view);
                    if(!item.disabled)
                        selectable.push(entry);
                    item.view.ui_elem.addEventListener('click', () => {
                        this._doChoose(entry);
                    });
                }
            }
            if(entries.length) {
                this.ui_add([
                    $el.DIV('ui-group-dropdown-header', g.name),
                    $el.DIV('ui-group-dropdown-items', entries.map(e => e.view)),
                ]);
            }
        }

        this._selectable = selectable;

        let key_down: ((ev: KeyboardEvent) => void) | undefined;
        let key_press: ((ev: KeyboardEvent) => void) | undefined;
        let search_input_handler: ((ev: Event) => void) | undefined;

        // TODO: move the key capture functionality to UIPopup
        onAttach(this, () => {
            document.addEventListener('keydown', key_down = ev => {
                if(ev.key === UIKey.Up) {
                    ev.preventDefault();
                    this.selectPrevious();
                } else if(ev.key === UIKey.Down) {
                    ev.preventDefault();
                    this.selectNext();
                } else if(ev.key === UIKey.Enter) {
                    if(this._selected >= 0) {
                        ev.preventDefault();
                        this._doChoose(selectable[this._selected]);
                    }
                }
            });

            if(options.search) {
                let query = '';

                document.addEventListener('keypress', key_press = ev => {
                    if(ev.charCode && ev.key !== UIKey.Enter) {
                        ev.preventDefault();
                        query = (query + String.fromCharCode(ev.charCode)).toLowerCase();

                        for(let i = 0; i < selectable.length; i++) {
                            const e = selectable[i];
                            if(e.search && e.search.toLowerCase().startsWith(query)) {
                                this._doSelect(i);
                                return;
                            }
                        }

                        for(let i = 0; i < selectable.length; i++) {
                            const e = selectable[i];
                            if(e.search && e.search.toLowerCase().includes(query)) {
                                this._doSelect(i);
                                return;
                            }
                        }

                        query = '';
                    }
                });
            } else if(options.search_input) {
                const input = options.search_input;
                input.element.addEventListener('input', search_input_handler = () => {
                    const query = input.text;
                    $log('app', 'dropdown search', query);
                    let first: number | undefined;
                    for(let i = 0; i < selectable.length; i++) {
                        const e = selectable[i];
                        const show = !e.search || e.search.toLowerCase().includes(query)
                        e.view?.ui_show(undefined, show);
                        e.hidden = !show;
                        if(show && !first)
                            first = i;
                    }

                    if(first != undefined)
                        this._doSelect(first);
                });
            }
        });

        onDetach(this, () => {
            key_press && document.removeEventListener('keypress', key_press);
            key_down && document.removeEventListener('keydown', key_down);
            search_input_handler && options.search_input!.element.removeEventListener('input', search_input_handler);
        });
    }

    get has_selection() {
        return this._selected >= 0;
    }

    selectPrevious() {
        if(!this._selectable.length) return;

        for(let i = this._selected - 1; i >= 0; i--) {
            if(!this._selectable[i].hidden) {
                this._doSelect(i);
                return;
            }
        }

        for(let i = this._selectable.length - 1; i >= this._selected + 1; i--) {
            if(!this._selectable[i].hidden) {
                this._doSelect(i);
                return;
            }
        }
    }

    selectNext() {
        if(!this._selectable.length) return;

        for(let i = this._selected + 1; i < this._selectable.length; i++) {
            if(!this._selectable[i].hidden) {
                this._doSelect(i);
                return;
            }
        }

        for(let i = 0; i < this._selected; i++) {
            if(!this._selectable[i].hidden) {
                this._doSelect(i);
                return;
            }
        }
    }

    private _doChoose(entry: Entry<Model>) {
        if(!entry.view) return;
        if(entry.on_choose?.() === false)
            return;
        if(this.on_choose?.(entry.model, entry.view) === false)
            return;
        if(!this._options.keep_open)
            this.close(true);
    }

    private _doSelect(n: number) {
        if(n == this._selected) return;

        if(this._selected >= 0) {
            const view = this._selectable[this._selected].view;
            view && view.classList.remove('f-selected')
        }

        this._selected = n;

        if(n < 0) return;

        const view = this._selectable[this._selected].view;
        if(!view) return;
        view.classList.add('f-selected');

        const elem = view.ui_elem;

        const top = elem.offsetTop;
        const bottom = top + elem.offsetHeight;

        if(top < this.root.scrollTop) {
            this.root.scrollTop = top;
        } else if(bottom > this.root.scrollTop + this.root.offsetHeight) {
            this.root.scrollTop = bottom - this.root.offsetHeight;
        }
    }

    open(ref: UIViewSpec, options: UIPopupOptions = { position: UIRelPos.Below | UIRelPos.End, capture_mouse: true, match_size: true, adjust_size: true, can_flip: true, prevent_blur: true }) {
        uiShowPopup(this, ref, options);
    }

    close(close_parent?: boolean) {
        uiHidePopup(this, close_parent);
    }
}
